import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  Typography,
  TextField,
  IconButton,
  Divider,
  Avatar,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { getHeader, useQuery } from "../../utility/utility";
import { API_ENDPOINT, USER_ROLES } from "../../constants/constants";
import MuPb from "../../widgets/MuPb";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import axios from "axios";
import { format } from "date-fns";
import MyChatList from "../dashboard/common/MyChatList";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";

// Dynamic display name helper based on Role
export const getDisplayName = (user) => {
  if (!user) return "Select a chat";

  if (user.role === USER_ROLES.EXHIBITOR && user.companyName?.value) {
    return user.companyName.value;
  }

  return (
    `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username
  );
};

// Helper to format roles like TRADE_DELEGATE to "Trade Delegate"
export const formatRole = (role) => {
  if (!role) return "";
  return role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

function ChatApp() {
  const query = useQuery();
  const id = query.get("tid");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [localMessages, setLocalMessages] = useState([]);
  const {
    data: chatData,
    isLoading: chatLoading,
    isError: isChatError,
    error: chatError,
  } = useApiEp(`/user/get-my-chat?id=${encodeURIComponent(id)}&`);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (chatData?.messages) {
      setLocalMessages(chatData.messages);
    }
  }, [chatData]);

  const messageBubbleStyle = (sent) => ({
    maxWidth: "75%",
    marginLeft: sent ? "auto" : 0,
    marginRight: sent ? 0 : "auto",
    backgroundColor: sent ? "#0ea5e9" : "#ffffff",
    color: sent ? "#ffffff" : "#334155",
    borderRadius: sent ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
    padding: "10px 16px",
    marginBottom: "4px",
    boxShadow: sent
      ? "0 4px 14px rgba(14, 165, 233, 0.25)"
      : "0 2px 8px rgba(0,0,0,0.04)",
    position: "relative",
    wordBreak: "break-word",
    border: sent ? "none" : "1px solid #eef2f6",
  });

  const onSendMessage = async () => {
    if (message.trim() === "") {
      toast.error("Please type the message to send");
      return;
    }
    try {
      const messageToSend = {
        recipient_id: chatData?.recipient?.username,
        message: message,
      };
      setSendingMsg(true);
      const response = await axios.post(
        `${API_ENDPOINT}/user/send-message-to`,
        messageToSend,
        getHeader()
      );

      setLocalMessages((prevMessages) => [
        ...prevMessages,
        {
          message: message,
          sender_id: chatData?.sender?.username,
          createdOn: new Date().toISOString(),
        },
      ]);

      setMessage("");
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMsg(false);
    }
  };

  if (chatLoading) {
    return <MuPb />;
  }

  return (
    <Box sx={{ pl: 2, pr: 2, pt: 1 }}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          sm={4}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: "calc(100vh - 82px)",
              overflowY: "auto",
              borderRadius: 4,
              border: "1px solid #eef2f6",
              boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box mb={2}>
              <BackBtn />
            </Box>
            <Box px={1}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search messages..."
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "24px",
                    backgroundColor: "#f8fafc",
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#0ea5e9" },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{ color: "#94a3b8", mr: 1, ml: 1, fontSize: 20 }}
                    />
                  ),
                }}
              />
            </Box>
            <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />
            <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1 }}>
              <MyChatList internal={true} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Paper
            elevation={0}
            sx={{
              height: "calc(100vh - 82px)",
              display: "flex",
              flexDirection: "column",
              borderRadius: 4,
              border: "1px solid #eef2f6",
              boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
              overflow: "hidden",
            }}
          >
            {/* Chat Header */}
            <Box
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#ffffff",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <Box display={"flex"} alignItems="center">
                <Avatar
                  alt={getDisplayName(chatData?.recipient)}
                  src={chatData?.recipient?.profile_image}
                  sx={{
                    marginRight: 2,
                    width: 44,
                    height: 44,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      fontSize: "1.1rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {getDisplayName(chatData?.recipient)}
                  </Typography>

                  {/* UPDATED: Displays Role • Active */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#64748b",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      mt: 0.5,
                    }}
                  >
                    {chatData?.recipient?.role && (
                      <span>{formatRole(chatData.recipient.role)}</span>
                    )}
                    {chatData?.recipient && (
                      <React.Fragment>
                        <span style={{ margin: "0 6px" }}>•</span>
                        <span style={{ color: "#10b981" }}>Active</span>
                      </React.Fragment>
                    )}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                sx={{
                  color: "#94a3b8",
                  "&:hover": { color: "#334155", backgroundColor: "#f1f5f9" },
                }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Box>

            {/* Messages Area */}
            <List
              sx={{
                overflowY: "auto",
                flexGrow: 1,
                px: { xs: 2, sm: 4 },
                py: 3,
                backgroundColor: "#fafafa",
              }}
            >
              {localMessages.map((message, index) => {
                const isSent = message.sender_id === chatData?.sender?.username;
                return (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <Box sx={messageBubbleStyle(isSent)}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {message.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          textAlign: isSent ? "right" : "left",
                          mt: 0.5,
                          color: isSent ? "rgba(255,255,255,0.8)" : "#94a3b8",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                        }}
                      >
                        {format(new Date(message.createdOn), "hh:mm a")}
                      </Typography>
                    </Box>
                  </ListItem>
                );
              })}
            </List>

            {/* Input Area */}
            <Box
              component="form"
              sx={{
                display: "flex",
                alignItems: "flex-end",
                p: 2,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <TextField
                fullWidth
                size="small"
                disabled={!id}
                multiline
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxRows={4}
                placeholder="Type a message..."
                variant="outlined"
                sx={{
                  mr: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    backgroundColor: "#f8fafc",
                    "& fieldset": { borderColor: "transparent" },
                    "&:hover fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#0ea5e9" },
                  },
                }}
              />
              <LoadingButton
                variant="contained"
                loading={sendingMsg}
                disabled={message.trim() === "" || !id}
                onClick={onSendMessage}
                sx={{
                  minWidth: "48px",
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  padding: 0,
                  backgroundColor: "#0ea5e9",
                  boxShadow: "0 4px 14px rgba(14, 165, 233, 0.4)",
                  "&:hover": {
                    backgroundColor: "#0284c7",
                    boxShadow: "0 6px 20px rgba(14, 165, 233, 0.6)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e2e8f0",
                    color: "#94a3b8",
                  },
                }}
              >
                {!sendingMsg && <SendIcon sx={{ fontSize: 20, ml: "4px" }} />}
              </LoadingButton>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ChatApp;
